import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import campaignService from "../services/campaignService";
import { getAgency, isWhiteLabel } from "../services/agencyService";
import { NewsletterEditionChangeRequest } from "../types/newsletterEdition";

const RECORDING_BAR_HEIGHTS = [...Array(10)].map(() => Math.random() * 20 + 10);

export interface GetFeedbackFormProps {
  setSidePanelOpen: (open: boolean) => void;
  setSubmitSuccess: (success: boolean) => void;
}

const GiveFeedbackForm: React.FC<GetFeedbackFormProps> = ({ setSidePanelOpen, setSubmitSuccess }) => {
  const { newsletterEditionId } = useParams();

  const [text, setText] = useState("");
  const [changeRequests, setChangeRequests] = useState<NewsletterEditionChangeRequest[]>([]);
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [makeChanges, setMakeChanges] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  useEffect(() => {
    if (isRecording && listening) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (listening) SpeechRecognition.stopListening();
    };
  }, [isRecording, listening]);

  const handleChange = (e: any) => {
    setText(e.target.value);
  }

  const startRecording = () => {
    if (!browserSupportsSpeechRecognition) return;
    resetTranscript();
    setRecordingTime(0);
    setIsRecording(true);
    SpeechRecognition.startListening({ continuous: true });
  };

  const stopRecording = () => {
    SpeechRecognition.stopListening();
    setIsRecording(false);
    if (transcript) {
      setText(prev => prev ? `${prev}\n\n${transcript}` : transcript);
    }
    resetTranscript();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const submitFeedback = async (comment: string) => {
    try {
      if (!comment) return;
      const newChangeRequest = await campaignService.submitNewsletterEditionFeedback(newsletterEditionId!, comment, makeChanges);
      setText("");
      setSidePanelOpen(false);
      setSubmitSuccess(true);
      setChangeRequests(prevRequests => [...prevRequests, newChangeRequest]);
    } catch (e) {
      console.error("Failed to submit feedback", e);
    }
  }

  useEffect(() => {
    if (!newsletterEditionId) return;
    campaignService.getNewsletterEdition(newsletterEditionId).then(newsletterEdition => {
      setChangeRequests([...newsletterEdition.newsletter_edition_change_requests]);
      setRating(newsletterEdition.rating ?? 0);
    });
  }, [newsletterEditionId]);

  return (
      <div>
          <h1>Feedback</h1>

          {browserSupportsSpeechRecognition && (
            <div style={{ marginBottom: "12px" }}>
              {!isRecording ? (
                <button
                  className="tertiary-info-button"
                  style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  onClick={startRecording}
                >
                  🎤 Record voice feedback
                </button>
              ) : (
                <div style={{ border: "1px solid #e5e7eb", borderRadius: "6px", padding: "12px", backgroundColor: "#fafafa" }}>
                  <div className="voice-modal-waveform" style={{ marginBottom: "8px" }}>
                    {RECORDING_BAR_HEIGHTS.map((height, i) => (
                      <div
                        key={i}
                        className="voice-modal-wave-bar"
                        style={{ height: `${height}px`, animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>{formatTime(recordingTime)}</div>
                  {transcript && (
                    <div style={{ fontSize: "13px", color: "#374151", marginBottom: "8px", fontStyle: "italic" }}>{transcript}</div>
                  )}
                  <button className="tertiary-info-button" style={{ cursor: "pointer" }} onClick={stopRecording}>
                    Stop recording
                  </button>
                </div>
              )}
            </div>
          )}

          <textarea
              value={text}
              onChange={handleChange}
              placeholder="What would you like us to change in this edition?"
              rows={8}
              cols={30}
              className="feedback-textarea-input"
          />

          <p style={{ margin: "0 0 12px 0", color: "#6b7280", fontSize: "14px" }}>{isWhiteLabel() ? "We turn" : "Potions turns"} around feedback within 2 business days, and carries it forward into future editions.<br/><br/>Even if you make a change yourself, letting us know what was off helps us improve what we build for you next time.</p>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <input
              type="checkbox"
              id="make-changes"
              checked={makeChanges}
              onChange={(e) => setMakeChanges(e.target.checked)}
              style={{ cursor: "pointer", width: "15px", height: "15px", flexShrink: 0 }}
            />
            <label htmlFor="make-changes" style={{ fontSize: "14px", color: "#374151", cursor: "pointer", userSelect: "none" }}>
              I'd like {isWhiteLabel() ? "the team" : "the Potions team"} to make these changes on my behalf
            </label>
            <div
              style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0 }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <span style={{ fontSize: "14px", color: "#9CA3AF", cursor: "help", lineHeight: 1 }}>ⓘ</span>
              {showTooltip && (
                <div style={{
                  position: "absolute", top: "50%", right: "calc(100% + 8px)", transform: "translateY(-50%)",
                  width: "230px", backgroundColor: "#1f2937", color: "#fff",
                  fontSize: "12px", padding: "8px 10px", borderRadius: "6px",
                  lineHeight: 1.5, zIndex: 20, pointerEvents: "none",
                }}>
                  If unchecked, we'll keep a note of this feedback for future reference but will not make the change on your behalf.
                </div>
              )}
            </div>
          </div>

          <button
              className="primary-button"
              onClick={() => submitFeedback(text)}>
              Submit
          </button>

          <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #e5e7eb" }}>
            <p style={{ margin: "0 0 4px 0", fontSize: "14px", color: "#374151", fontWeight: 500 }}>Rate this edition</p>
            <p style={{ margin: "0 0 10px 0", fontSize: "12px", color: "#6b7280" }}>How would you rate the edition we produced for you, before any of your own edits? We'll use this to improve future editions.</p>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {[1, 2, 3, 4, 5].map(star => (
                <span
                  key={star}
                  onClick={() => {
                    setRating(star);
                    campaignService.rateNewsletterEdition(newsletterEditionId!, star);
                  }}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    cursor: "pointer",
                    fontSize: "28px",
                    color: star <= (hoverRating || rating) ? "#f59e0b" : "#d1d5db",
                    lineHeight: 1,
                    userSelect: "none",
                  }}
                >
                  ★
                </span>
              ))}
            </div>
          </div>

          {getAgency() !== 'authoritydigest' && (
            <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #e5e7eb", fontSize: "13px", color: "#6b7280" }}>
              Prefer talking it through?{" "}
              <a
                href="https://calendly.com/d/cvd7-wp3-nty/potions-intro-email"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#5d5ded", textDecoration: "underline" }}
              >
                Book a 15-min call
              </a>
              {" "}and we'll go over it together.
            </div>
          )}

          {changeRequests.length > 0 && (
            <>
              <br/><br/>
              <p>Change Request History:</p>
              <div className="change-request-container">
                {changeRequests.map((request: NewsletterEditionChangeRequest, index: number) => (
                  <div key={index} className="change-request-item" style={request.has_been_addressed ? { borderLeft: '3px solid #22c55e' } : {}}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <span>{request.request_text}</span>
                      {request.has_been_addressed ? (
                        <span style={{ color: '#22c55e', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>Addressed</span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>Pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
      </div>
  );
};

export default GiveFeedbackForm;
